import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home,
  LayoutGrid,
  RefreshCw,
  Wallet,
  Building,
  Settings,
} from "lucide-react";

// Simple className utility function
const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(" ");
};

const navItems = [
  { path: "/platform", icon: Home, label: "Home" },
  { path: "/primarymarketplace", icon: LayoutGrid, label: "Invest" },
  { path: "/secondarymarketplace", icon: RefreshCw, label: "Trade" },
  { path: "/rental", icon: Building, label: "Yield" },
  { path: "/user/profile", icon: Wallet, label: "Portfolio" },
  { path: "/user/profile/settings", icon: Settings, label: "Settings" },
];

export const MobileBottomNav = () => {
  const location = useLocation();

  // Hide on auth, onboarding, detail pages, and user layout pages (user pages have their own navigation)
  // But keep visible on profile and settings pages
  const hidePaths = ["/auth", "/onboarding"];
  const allowedUserPaths = ["/user/profile", "/user/profile/settings"];
  const isAllowedUserPath = allowedUserPaths.some(
    (path) =>
      location.pathname === path || location.pathname.startsWith(path + "/")
  );

  const shouldHide =
    hidePaths.some((path) => location.pathname.startsWith(path)) ||
    (location.pathname.startsWith("/user") && !isAllowedUserPath) ||
    location.pathname.startsWith("/primaryPropertyDetails") ||
    location.pathname.startsWith("/propertyDetails") ||
    location.pathname.startsWith("/rental/property") ||
    location.pathname.startsWith("/collection-detail") ||
    location.pathname.startsWith("/creator") ||
    location.pathname.startsWith("/collection-edit") ||
    location.pathname.startsWith("/edit-profile");

  if (shouldHide) {
    return null;
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
      {/* Backdrop blur effect */}
      <div
        className="absolute inset-0 border-t"
        style={{
          backgroundColor: "rgba(13, 17, 23, 0.8)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderColor: "rgba(48, 54, 61, 0.5)",
        }}
      />

      <div className="relative flex items-center justify-around px-2 py-2 safe-area-bottom">
        {navItems.map((item) => {
          // Check if current path matches or starts with item path
          let isActive = false;

          if (item.path === "/platform") {
            // Special handling for platform/home
            isActive =
              location.pathname === "/platform" || location.pathname === "/";
          } else if (item.path === "/primarymarketplace") {
            // Check for both /marketplace (canonical) and /primarymarketplace (legacy)
            isActive =
              location.pathname === "/marketplace" ||
              location.pathname === "/primarymarketplace" ||
              location.pathname.startsWith("/primarymarketplace");
          } else if (item.path === "/secondarymarketplace") {
            // Check for secondary marketplace paths
            isActive =
              location.pathname === "/secondarymarketplace" ||
              location.pathname.startsWith("/secondarymarketplace") ||
              location.pathname.startsWith("/secondary-marketplace") ||
              location.pathname === "/secondary";
          } else if (item.path === "/user/profile") {
            // For profile, check if we're on any user page (but not settings)
            isActive =
              location.pathname.startsWith("/user") &&
              !location.pathname.startsWith("/user/profile/settings");
          } else if (item.path === "/user/profile/settings") {
            // For settings, check if we're on the settings page
            isActive =
              location.pathname === "/user/profile/settings" ||
              location.pathname.startsWith("/user/profile/settings");
          } else {
            // For other paths, check if pathname matches or starts with item path
            isActive =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path);
          }

          const active = isActive;

          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "relative flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all duration-200",
                active
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
              style={{
                color: active ? "#BAE325" : "#8B949E",
              }}
            >
              {active && (
                <motion.div
                  layoutId="bottomNavIndicator"
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "0.75rem",
                    backgroundColor: "rgba(186, 227, 37, 0.1)",
                  }}
                  transition={{
                    type: "spring",
                    bounce: 0.2,
                    duration: 0.6,
                  }}
                />
              )}
              <item.icon
                className={cn(
                  "w-5 h-5 relative z-10 transition-transform",
                  active && "scale-110"
                )}
              />
              <span
                className={cn(
                  "text-[10px] font-medium relative z-10",
                  active && "font-semibold"
                )}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
